/* eslint @typescript-eslint/no-explicit-any: 0 */

import { useCreateUserMutation } from "../../features/api/userSlice";
import { useAuth } from "../contexts/auth.context";
import React, { useState } from "react";
import { Box, Button, Stack, TextField, Typography, IconButton, InputAdornment } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate, Navigate } from "react-router-dom";

const SignUp: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [username, setUsername] = useState("");
    const [error, setError] = useState("");
    const [createUser, { isLoading: loadingCreateUser }] = useCreateUserMutation();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const validateEmail = (email: string) => {
        if (/^[a-zA-Z0-9]+@(?:[a-zA-Z0-9]+\.)+[A-Za-z]+$/.test(email)) {
            return true;
        }
        return false;
    };

    const validatePassword = (password: string) => {
        const e = "Password requirement not met: ";

        if (password.length < 8) {
            setError(e + "at least 8 characters required");
            return false;
        }
        if (password.length > 24) {
            setError(e + "do not exceed 24 characters");
            return false;
        }
        if (!/[a-zA-Z]/.test(password)) {
            setError(e + "include at least 1 letter in the password");
            return false;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return false;
        }
        return true;
    };

    const validateUsername = (username: string) => {
        if (username.length > 30) {
            setError("Username must not exceed 30 characters.");
            return false;
        }
        return true;
    };

    const handleSignUp = async () => {
        if (!validateEmail(email)) {
            setError("Please enter a valid email");
            return;
        }

        if (!validatePassword(password)) {
            return;
        }

        if (!validateUsername(username)) {
            return;
        }

        if (!loadingCreateUser) {
            try {
                setError("Creating user...");
                await createUser({ email, password, username }).unwrap();
                navigate("/SignIn");
            } catch (error: any) {
                console.log(error);
                if (error.status === 400) {
                    const emailError: string[] = error.data.email || [];
                    const passwordError: string[] = error.data.password || [];
                    const errorMsg = [...emailError, ...passwordError].join("\n");
                    setError(errorMsg);
                } else {
                    setError("Failed to create user.");
                }
            }
        }
    };

    if (user.id !== -1) {
        return <Navigate to="/" />;
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100vh"
            sx={{ bgcolor: "#E8FDEA" }}
        >
            <IconButton onClick={() => navigate("/SignIn")} sx={{ position: "absolute", top: 20, left: 20 }}>
                <ArrowBackIcon />
            </IconButton>
            <Typography
                variant="h3"
                color=""
                marginBottom={2}
                sx={{
                    textShadow: `
                1px 1px 1px rgba(0, 0, 0, 0.1),
                2px 2px 2px rgba(0, 0, 0, 0.2),
                3px 3px 3px rgba(0, 0, 0, 0.3)`,
                }}
            >
                <b>Sign Up</b>
            </Typography>
            {error && (
                <Typography color="error" marginBottom={3} whiteSpace="pre-line">
                    {error}
                </Typography>
            )}
            <Stack
                sx={{
                    width: "30ch",
                }}
            >
                <TextField
                    required
                    label="Username"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                />
                <TextField
                    required
                    label="Email"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                    required
                    label="Password"
                    variant="outlined"
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    margin="normal"
                    value={password}
                    onChange={(e) => {
                        setPassword(e.target.value);
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={togglePasswordVisibility}>
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    required
                    label="Confirm Password"
                    variant="outlined"
                    type={showConfirmPassword ? "text" : "password"}
                    fullWidth
                    margin="normal"
                    value={confirmPassword}
                    onChange={(e) => {
                        setConfirmPassword(e.target.value);
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={toggleConfirmPasswordVisibility}>
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Stack>
            <Button
                variant="contained"
                color="primary"
                endIcon={<SendIcon />}
                onClick={handleSignUp}
                sx={{ marginTop: 2 }}
                disabled={!email || !username || !password || !confirmPassword}
            >
                Sign Up
            </Button>
            <Button
                variant="text"
                color="secondary"
                endIcon={<HomeIcon />}
                onClick={() => navigate("/")}
                size="large"
                sx={{ marginTop: 20 }}
            >
                Home
            </Button>
        </Box>
    );
};

export default SignUp;
